import * as THREE from 'three'

/**
 * Builds a Roblox-style blocky 3D skier character.
 * Big square head, cylindrical arms/legs, boxy torso.
 * Returns a THREE.Group that can be added to any scene.
 */
export function createSkierModel(): THREE.Group {
  const skier = new THREE.Group()

  // --- Materials ---
  const skinMat = new THREE.MeshStandardMaterial({ color: 0xf5c6a0, roughness: 0.6 })
  const jacketMat = new THREE.MeshStandardMaterial({ color: 0x1a5fb4, roughness: 0.5 })
  const pantsMat = new THREE.MeshStandardMaterial({ color: 0x1c1c1c, roughness: 0.6 })
  const bootMat = new THREE.MeshStandardMaterial({ color: 0x333333, roughness: 0.4 })
  const helmetMat = new THREE.MeshStandardMaterial({ color: 0xe0e0e0, roughness: 0.3 })
  const goggleMat = new THREE.MeshStandardMaterial({ color: 0xff6600, roughness: 0.1, metalness: 0.7 })
  const eyeMat = new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.5 })
  const skiMat = new THREE.MeshStandardMaterial({ color: 0xcc0000, roughness: 0.3, metalness: 0.2 })
  const poleMat = new THREE.MeshStandardMaterial({ color: 0x999999, metalness: 0.8, roughness: 0.2 })
  const gloveMat = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.5 })
  const bindingMat = new THREE.MeshStandardMaterial({ color: 0x444444, roughness: 0.3 })

  // =====================
  // HEAD (big blocky cube — Roblox signature)
  // =====================
  const headGroup = new THREE.Group()

  // Full helmet covering the entire head (slightly larger than head)
  const helmet = new THREE.Mesh(
    new THREE.BoxGeometry(0.68, 0.68, 0.68),
    helmetMat
  )
  headGroup.add(helmet)

  // Helmet edge / chin line (darker strip at bottom)
  const helmetRim = new THREE.Mesh(
    new THREE.BoxGeometry(0.69, 0.04, 0.69),
    new THREE.MeshStandardMaterial({ color: 0xaaaaaa, roughness: 0.3 })
  )
  helmetRim.position.set(0, -0.32, 0)
  headGroup.add(helmetRim)

  // Helmet vents (top detail)
  for (let i = -1; i <= 1; i++) {
    const vent = new THREE.Mesh(
      new THREE.BoxGeometry(0.06, 0.015, 0.3),
      new THREE.MeshStandardMaterial({ color: 0x666666, roughness: 0.4 })
    )
    vent.position.set(i * 0.12, 0.345, 0)
    headGroup.add(vent)
  }

  // Face area (skin-colored inset on the front of helmet)
  const face = new THREE.Mesh(
    new THREE.BoxGeometry(0.5, 0.35, 0.02),
    skinMat
  )
  face.position.set(0, -0.08, 0.34)
  headGroup.add(face)

  // Goggles (sitting on the eyes area)
  const goggleFrame = new THREE.Mesh(
    new THREE.BoxGeometry(0.52, 0.16, 0.06),
    new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.4 })
  )
  goggleFrame.position.set(0, 0.02, 0.35)
  headGroup.add(goggleFrame)

  // Goggle left lens
  const leftLens = new THREE.Mesh(
    new THREE.BoxGeometry(0.18, 0.12, 0.02),
    goggleMat
  )
  leftLens.position.set(-0.12, 0.02, 0.38)
  headGroup.add(leftLens)

  // Goggle right lens
  const rightLens = leftLens.clone()
  rightLens.position.set(0.12, 0.02, 0.38)
  headGroup.add(rightLens)

  // Goggle bridge (between lenses)
  const goggleBridge = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 0.06, 0.02),
    new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.4 })
  )
  goggleBridge.position.set(0, 0.02, 0.38)
  headGroup.add(goggleBridge)

  // Goggle strap (wraps around helmet sides and back)
  const strapMat = new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.5 })
  const strapLeft = new THREE.Mesh(
    new THREE.BoxGeometry(0.02, 0.08, 0.34),
    strapMat
  )
  strapLeft.position.set(-0.345, 0.02, 0.17)
  headGroup.add(strapLeft)

  const strapRight = strapLeft.clone()
  strapRight.position.set(0.345, 0.02, 0.17)
  headGroup.add(strapRight)

  const strapBack = new THREE.Mesh(
    new THREE.BoxGeometry(0.5, 0.06, 0.02),
    strapMat
  )
  strapBack.position.set(0, 0.02, -0.345)
  headGroup.add(strapBack)

  // Smile (on the face area, below goggles)
  const smileCurve = new THREE.EllipseCurve(0, 0, 0.1, 0.05, Math.PI, 2 * Math.PI, false, 0)
  const smilePoints = smileCurve.getPoints(20)
  const smileGeo = new THREE.BufferGeometry().setFromPoints(smilePoints)
  const smileLine = new THREE.Line(
    smileGeo,
    new THREE.LineBasicMaterial({ color: 0x222222, linewidth: 2 })
  )
  smileLine.position.set(0, -0.18, 0.35)
  headGroup.add(smileLine)

  headGroup.position.set(0, 1.95, 0)
  skier.add(headGroup)

  // =====================
  // TORSO (rectangular block — Roblox style)
  // =====================
  const torso = new THREE.Mesh(
    new THREE.BoxGeometry(0.65, 0.65, 0.4),
    jacketMat
  )
  torso.position.set(0, 1.3, 0)
  skier.add(torso)

  // Jacket zipper line
  const zipper = new THREE.Mesh(
    new THREE.BoxGeometry(0.02, 0.6, 0.01),
    poleMat
  )
  zipper.position.set(0, 1.3, 0.205)
  skier.add(zipper)

  // =====================
  // ARMS (cylinders — Roblox style)
  // =====================
  const createArm = (side: number) => {
    const armGroup = new THREE.Group()

    // Upper arm (jacket color)
    const upperArm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.1, 0.4, 8),
      jacketMat
    )
    upperArm.position.set(side * 0.43, 1.4, 0.08)
    upperArm.rotation.x = -0.4 // angled forward for ski stance
    upperArm.rotation.z = side * 0.15
    armGroup.add(upperArm)

    // Lower arm / forearm
    const forearm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.09, 0.09, 0.35, 8),
      jacketMat
    )
    forearm.position.set(side * 0.48, 1.1, 0.22)
    forearm.rotation.x = -0.7
    forearm.rotation.z = side * 0.1
    armGroup.add(forearm)

    // Glove (blocky cube hand — Roblox style)
    const glove = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.12, 0.12),
      gloveMat
    )
    glove.position.set(side * 0.48, 0.92, 0.38)
    armGroup.add(glove)

    return armGroup
  }

  skier.add(createArm(1))
  skier.add(createArm(-1))

  // =====================
  // SKI POLES
  // =====================
  const createPole = (side: number) => {
    const poleGroup = new THREE.Group()

    const shaft = new THREE.Mesh(
      new THREE.CylinderGeometry(0.015, 0.015, 1.3, 6),
      poleMat
    )
    shaft.position.set(side * 0.48, 0.4, 0.38)
    shaft.rotation.x = -0.15
    poleGroup.add(shaft)

    // Grip
    const grip = new THREE.Mesh(
      new THREE.CylinderGeometry(0.025, 0.02, 0.1, 6),
      new THREE.MeshStandardMaterial({ color: 0x222222 })
    )
    grip.position.set(side * 0.48, 0.98, 0.36)
    poleGroup.add(grip)

    // Basket
    const basket = new THREE.Mesh(
      new THREE.TorusGeometry(0.05, 0.008, 6, 12),
      poleMat
    )
    basket.position.set(side * 0.48, -0.18, 0.42)
    basket.rotation.x = Math.PI / 2
    poleGroup.add(basket)

    return poleGroup
  }

  skier.add(createPole(1))
  skier.add(createPole(-1))

  // =====================
  // LEGS (cylinders — Roblox style)
  // =====================
  const createLeg = (side: number) => {
    const legGroup = new THREE.Group()

    // Upper leg
    const thigh = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.11, 0.4, 8),
      pantsMat
    )
    thigh.position.set(side * 0.15, 0.72, 0.04)
    thigh.rotation.x = 0.2
    legGroup.add(thigh)

    // Lower leg
    const shin = new THREE.Mesh(
      new THREE.CylinderGeometry(0.11, 0.1, 0.4, 8),
      pantsMat
    )
    shin.position.set(side * 0.15, 0.35, 0.08)
    shin.rotation.x = -0.15
    legGroup.add(shin)

    // Ski boot (blocky box — Roblox style)
    const boot = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.16, 0.28),
      bootMat
    )
    boot.position.set(side * 0.15, 0.1, 0.1)
    legGroup.add(boot)

    return legGroup
  }

  skier.add(createLeg(1))
  skier.add(createLeg(-1))

  // =====================
  // SKIS (flat boxes — keeping it blocky)
  // =====================
  const createSki = (side: number) => {
    const skiGroup = new THREE.Group()

    // Ski body — long flat box
    const ski = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.03, 1.8),
      skiMat
    )
    ski.position.set(side * 0.15, 0.015, -0.1)
    skiGroup.add(ski)

    // Ski tip (angled front)
    const tip = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.03, 0.15),
      skiMat
    )
    tip.position.set(side * 0.15, 0.05, -0.9)
    tip.rotation.x = 0.5
    skiGroup.add(tip)

    // Ski tail (slight upturn)
    const tail = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.03, 0.1),
      skiMat
    )
    tail.position.set(side * 0.15, 0.03, 0.85)
    tail.rotation.x = -0.3
    skiGroup.add(tail)

    // Binding
    const binding = new THREE.Mesh(
      new THREE.BoxGeometry(0.11, 0.05, 0.14),
      bindingMat
    )
    binding.position.set(side * 0.15, 0.045, 0.1)
    skiGroup.add(binding)

    // Ski base stripe (decorative)
    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(0.04, 0.035, 1.6),
      new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4 })
    )
    stripe.position.set(side * 0.15, 0.015, -0.1)
    skiGroup.add(stripe)

    return skiGroup
  }

  skier.add(createSki(1))
  skier.add(createSki(-1))

  // Enable shadows on all mesh children
  skier.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.castShadow = true
      child.receiveShadow = true
    }
  })

  return skier
}
